import React, { useState } from "react";
import {
    ChevronDown, ChevronRight, Sparkles, Loader2, Save, ShieldQuestion,
    FileWarning, Wrench, Landmark,
} from "lucide-react";
import { toast } from "sonner";
import { SeverityBadge } from "@/components/common/Badges";
import { api } from "@/lib/api";
import { TID } from "@/lib/testIds";

const STATUS_LABELS = {
    open: "Open",
    fixed: "Fixed",
    false_positive: "Rejected — false positive",
    accepted_risk: "Accepted risk",
    requires_sap_validation: "Requires SAP validation",
    requires_functional_confirmation: "Requires functional confirmation",
    requires_security_approval: "Requires security approval",
};

const STATUS_TONE = {
    open: "bg-rose-500/10 text-rose-300 border-rose-500/25",
    fixed: "bg-emerald-500/10 text-emerald-300 border-emerald-500/25",
    false_positive: "bg-slate-500/10 text-slate-300 border-slate-500/25",
    accepted_risk: "bg-amber-500/10 text-amber-300 border-amber-500/25",
    requires_sap_validation: "bg-cyan-500/10 text-cyan-300 border-cyan-500/25",
    requires_functional_confirmation: "bg-cyan-500/10 text-cyan-300 border-cyan-500/25",
    requires_security_approval: "bg-violet-500/10 text-violet-300 border-violet-500/25",
};

/** Simple two-column "diff" — offending statement vs corrected pattern. */
function CodeBlock({ label, code, tone }) {
    return (
        <div className="min-w-0">
            <div className={`text-[9px] font-mono uppercase tracking-wider mb-1 ${tone}`}>{label}</div>
            <pre className="text-[11px] font-mono leading-relaxed text-slate-200 bg-[#05070E] border border-white/10 rounded-[4px] p-2.5 overflow-x-auto whitespace-pre-wrap break-words">
                {code || "—"}
            </pre>
        </div>
    );
}

export function CodeFindingCard({ finding, sessionId, statuses, atcConfirmed = false, onUpdated }) {
    const [open, setOpen] = useState(false);
    const [status, setStatus] = useState(finding.status || "open");
    const [comment, setComment] = useState(finding.comment || "");
    const [waiver, setWaiver] = useState(finding.waiver_justification || "");
    const [approver, setApprover] = useState(finding.approver || "");
    const [transportRef, setTransportRef] = useState(finding.transport_reference || "");
    const [saving, setSaving] = useState(false);
    const [refining, setRefining] = useState(false);
    const [aiCode, setAiCode] = useState(finding.ai_corrected_code || "");

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.put(`/code-review/sessions/${sessionId}/finding-state`, {
                key: finding.key, status, comment, waiver_justification: waiver,
                approver, transport_reference: transportRef, ai_corrected_code: aiCode,
            });
            toast.success("Finding updated — clearance recomputed");
            onUpdated?.(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally { setSaving(false); }
    };

    const refine = async () => {
        setRefining(true);
        try {
            const { data } = await api.post(`/code-review/sessions/${sessionId}/refine`, { key: finding.key });
            setAiCode(data.ai_corrected_code);
            toast.success("UTSAVA Reasoner refined the corrected code");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "AI refine unavailable");
        } finally { setRefining(false); }
    };

    const isAtcEq = (finding.classification || "").startsWith("Engineering Engine");

    return (
        <div data-testid={TID.crFindingRow(finding.key)} className="panel rounded-[4px] border border-white/5">
            <button
                data-testid={TID.crFindingExpand(finding.key)}
                onClick={() => setOpen((o) => !o)}
                className="w-full flex items-start gap-3 p-3 text-left hover:bg-white/[0.02] transition-colors"
            >
                <span className="text-slate-500 mt-0.5 shrink-0">{open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</span>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-[10px] text-cyan-300">{finding.rule_id}</span>
                        <SeverityBadge severity={finding.severity} />
                        <span className="text-xs text-white font-medium">{finding.title}</span>
                        <span className={`text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${STATUS_TONE[status] || STATUS_TONE.open}`}>
                            {STATUS_LABELS[status] || status}
                        </span>
                        {atcConfirmed && (
                            <span
                                data-testid={TID.crFindingAtcConfirmed(finding.key)}
                                title="SAP's own ATC export flagged the same source line"
                                className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm border bg-emerald-500/10 text-emerald-300 border-emerald-500/30"
                            >
                                SAP ATC confirmed
                            </span>
                        )}
                    </div>
                    <div className="text-[10px] font-mono text-slate-500 mt-1">
                        {finding.object_context} · lines {finding.line_range} · {finding.category}
                    </div>
                </div>
            </button>

            {open && (
                <div className="px-3 pb-3 space-y-3 border-t border-white/5 pt-3">
                    <div className={`text-[10px] font-mono inline-flex items-center gap-1.5 px-2 py-1 rounded-sm border ${isAtcEq ? "bg-amber-500/10 text-amber-300 border-amber-500/25" : "bg-cyan-500/10 text-cyan-300 border-cyan-500/25"}`}>
                        <ShieldQuestion size={11} /> {finding.classification}
                    </div>

                    <p className="text-[12px] text-slate-300 leading-relaxed">{finding.message}</p>

                    <div className="grid md:grid-cols-2 gap-3">
                        {finding.root_cause && (
                            <div><div className="text-[9px] font-mono uppercase tracking-wider text-slate-500 mb-0.5">Root cause</div>
                                <p className="text-[11px] text-slate-400">{finding.root_cause}</p></div>
                        )}
                        {finding.business_impact && (
                            <div><div className="text-[9px] font-mono uppercase tracking-wider text-slate-500 mb-0.5">Business impact</div>
                                <p className="text-[11px] text-slate-400">{finding.business_impact}</p></div>
                        )}
                    </div>

                    {(finding.su24_impact || finding.pfcg_impact || finding.fiori_odata_impact || finding.authorization_impact) && (
                        <div className="flex flex-wrap gap-2">
                            {finding.authorization_impact && <Impact icon={FileWarning} label="Authorization" text={finding.authorization_impact} />}
                            {finding.su24_impact && <Impact icon={Wrench} label="SU24" text={finding.su24_impact} />}
                            {finding.pfcg_impact && <Impact icon={Landmark} label="PFCG" text={finding.pfcg_impact} />}
                            {finding.fiori_odata_impact && <Impact icon={Landmark} label="Fiori / OData" text={finding.fiori_odata_impact} />}
                        </div>
                    )}

                    <div className="grid md:grid-cols-2 gap-3">
                        <CodeBlock label="Offending statement" code={finding.statement} tone="text-rose-300" />
                        <CodeBlock label={aiCode ? "AI-refined corrected code" : "Corrected secure pattern"} code={aiCode || finding.corrected_code} tone="text-emerald-300" />
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                        <button
                            data-testid={TID.crFindingRefineBtn(finding.key)}
                            onClick={refine}
                            disabled={refining}
                            className="btn-ghost inline-flex items-center gap-1.5 disabled:opacity-50"
                            title="Ask the UTSAVA Reasoner to refine the corrected code"
                        >
                            {refining ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} AI-refine corrected code
                        </button>
                        {finding.remediation && <span className="text-[10px] font-mono text-slate-500">Remediation: {finding.remediation}</span>}
                    </div>

                    {finding.test_scenario && (
                        <p className="text-[10px] font-mono text-slate-500">Test scenario: {finding.test_scenario}</p>
                    )}
                    {(finding.references || []).length > 0 && (
                        <p className="text-[10px] font-mono text-slate-400">Refs: {finding.references.join(" · ")}</p>
                    )}

                    {/* Reviewer workflow */}
                    <div className="grid md:grid-cols-2 gap-3 pt-2 border-t border-white/5">
                        <div>
                            <label className="label-eyebrow block mb-1">Status</label>
                            <select
                                data-testid={TID.crFindingStatusSelect(finding.key)}
                                value={status}
                                onChange={(e) => setStatus(e.target.value)}
                                className="field w-full"
                            >
                                {statuses.map((s) => <option key={s} value={s}>{STATUS_LABELS[s] || s}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="label-eyebrow block mb-1">Transport reference</label>
                            <input value={transportRef} onChange={(e) => setTransportRef(e.target.value)} placeholder="DEVK9xxxxx" className="field w-full font-mono" />
                        </div>
                        <div className="md:col-span-2">
                            <label className="label-eyebrow block mb-1">Reviewer comment / evidence</label>
                            <textarea
                                data-testid={TID.crFindingComment(finding.key)}
                                value={comment} onChange={(e) => setComment(e.target.value)}
                                rows={2} className="field w-full resize-y" placeholder="Evidence, retest result, decision rationale…"
                            />
                        </div>
                        {(status === "accepted_risk" || status === "false_positive") && (
                            <>
                                <div>
                                    <label className="label-eyebrow block mb-1">Waiver justification</label>
                                    <textarea value={waiver} onChange={(e) => setWaiver(e.target.value)} rows={2} className="field w-full resize-y" />
                                </div>
                                <div>
                                    <label className="label-eyebrow block mb-1">Approver</label>
                                    <input value={approver} onChange={(e) => setApprover(e.target.value)} placeholder="security.lead@org" className="field w-full font-mono" />
                                </div>
                            </>
                        )}
                    </div>

                    <button
                        data-testid={TID.crFindingSaveBtn(finding.key)}
                        onClick={save}
                        disabled={saving}
                        className="btn-primary inline-flex items-center gap-1.5 disabled:opacity-50"
                    >
                        {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save & recompute clearance
                    </button>
                </div>
            )}
        </div>
    );
}

function Impact({ icon: Icon, label, text }) {
    return (
        <span title={text} className="inline-flex items-center gap-1 text-[10px] font-mono px-2 py-1 rounded-sm bg-white/5 text-slate-300 border border-white/10 max-w-full">
            <Icon size={10} className="text-cyan-400 shrink-0" />
            <span className="font-semibold text-cyan-300">{label}:</span>
            <span className="truncate">{text}</span>
        </span>
    );
}
